import { StoryblokMissingField } from "@octopus-energy/coral/storyblok"
import { type CoralSpacing } from "@octopus-energy/coral-design"
import { token } from "@octopus-energy/coral-design/tokens"
import { type SystemStyleObject } from "@octopus-energy/coral-design/types"
import { storyblokEditable } from "@storyblok/react"
import { render } from "storyblok-rich-text-react-renderer-ts"
import { type SbBlokTypography } from "../../types"
import { getDefaultStringResolver } from "../../utils/getDefaultStringResolver"
import { getMarkResolvers } from "../../utils/getMarkResolvers"
import { getNodeResolvers } from "../../utils/getNodeResolvers"
import { getTypographyColor } from "../../utils/getTypographyColor"
import { css, cva } from "@octopus-energy/coral-design/css"

const typographyRecipe = cva({
    variants: {
        gap: {
            none: { "& > *": { marginBottom: token("spacing.none") } },
            xxs: { "& > *": { marginBottom: token("spacing.xxs") } },
            xs: { "& > *": { marginBottom: token("spacing.xs") } },
            xsSm: { "& > *": { marginBottom: token("spacing.xsSm") } },
            sm: { "& > *": { marginBottom: token("spacing.sm") } },
            smMd: { "& > *": { marginBottom: token("spacing.smMd") } },
            md: { "& > *": { marginBottom: token("spacing.md") } },
            mdLg: { "& > *": { marginBottom: token("spacing.mdLg") } },
            lg: { "& > *": { marginBottom: token("spacing.lg") } },
            lgXl: { "& > *": { marginBottom: token("spacing.lgXl") } },
            xl: { "& > *": { marginBottom: token("spacing.xl") } },
            xxl: { "& > *": { marginBottom: token("spacing.xxl") } },
            xxxl: { "& > *": { marginBottom: token("spacing.xxxl") } },
        } satisfies Record<keyof CoralSpacing, SystemStyleObject>,
    },
})

interface DynamicTypographyProps {
    blok: SbBlokTypography
}

export default function DynamicTypography({ blok }: DynamicTypographyProps) {
    if (!blok?.content) {
        return (
            <StoryblokMissingField
                blok="Typography"
                field="Content"
            />
        )
    }

    return (
        <div
            {...storyblokEditable(blok)}
            className={`${typographyRecipe({ gap: blok.gap })} ${css({ textAlign: blok.align })}`}
        >
            {render(blok.content, {
                markResolvers: getMarkResolvers(),
                nodeResolvers: getNodeResolvers(blok.color && getTypographyColor(blok.color)),
                defaultStringResolver: getDefaultStringResolver,
            })}
        </div>
    )
}
